import * as Linking from 'expo-linking';
import * as WebBrowser from 'expo-web-browser';
import i18n from '@/utils/i18n';
import { AlertLevel, CreditMemoInput, InvoiceInput } from '@/types';
import { captureException } from '@/utils/sentry';
import { PaymentBatchResultCode, PaymentMethodType } from '@/enums';
import { useCallback } from 'react';
import { useConfirmKlarnaPayment } from '@/hooks/payments/use-confirm-klarna-payment';
import { useInitiateKlarnaPayment } from '@/hooks/payments/use-initiate-klarna-payment';
import { useKlarnaStore, variantOf } from '@/stores/use-klarna-store';
import { useToasts } from '@/contexts/toast-context';

interface UseKlarnaNativeFlowArgs {
  onKlarnaPayment: () => void;
  refreshQueries: () => void;
  restaurantUUID: string;
  supplierUUID: string;
}

interface StartFlowArgs {
  creditMemos: CreditMemoInput[];
  feesInMicros: number;
  invoices: InvoiceInput[];
}

/**
 * Runs the Klarna payment flow on native when the user taps "Continue
 * with Klarna."
 *
 * This flow initiates and then opens Klarna's hosted checkout in the
 * system WebBrowser. Adyen auto-captures but we call the confirm
 * mutation to check the transaction status so we can update the UI.
 *
 * @param onKlarnaPayment - Runs after Klarna completes.
 * @param refreshQueries - Function to refresh unpaid invoices and payment history.
 * @param restaurantUUID - Restaurant paying.
 * @param supplierUUID - Distributor being paid.
 * @returns `{ startFlow }`. Starts the flow. Called with the selected
 *   invoices, credit memos, and transaction fee.
 *
 * @example
 * const { startFlow: runKlarnaNativeFlow } = useKlarnaNativeFlow({
 *   onKlarnaPayment: () => setShowSuccessModal(true),
 *   refreshQueries,
 *   restaurantUUID,
 *   supplierUUID,
 * });
 * await runKlarnaNativeFlow({ invoices, creditMemos, feesInMicros });
 */
export const useKlarnaNativeFlow = ({
  onKlarnaPayment,
  refreshQueries,
  restaurantUUID,
  supplierUUID,
}: UseKlarnaNativeFlowArgs) => {
  const klarnaSelection = useKlarnaStore((s) => s.selection);

  const [initiate] = useInitiateKlarnaPayment();
  const [confirm] = useConfirmKlarnaPayment();

  const { showErrorToast } = useToasts();

  const startFlow = useCallback(
    async ({
      creditMemos,
      feesInMicros,
      invoices,
    }: StartFlowArgs): Promise<boolean> => {
      const variant = variantOf(klarnaSelection);
      if (!variant) {
        return false;
      }

      const returnUrl = Linking.createURL('klarna_return');

      const paymentMethodJson = JSON.stringify({
        data: {
          paymentMethod: {
            type:
              variant === PaymentMethodType.KLARNA
                ? 'klarna'
                : 'klarna_account',
          },
        },
      });
      const { data } = await initiate({
        variables: {
          creditMemos,
          feesInMicros,
          invoices,
          paymentMethod: paymentMethodJson,
          restaurantId: restaurantUUID,
          returnUrl,
          supplierId: supplierUUID,
        },
      });
      const response = data?.initiateKlarnaPayment;
      if (
        !response ||
        response.alert_level !== AlertLevel.Positive ||
        !response.payment_batch_id
      ) {
        showErrorToast(response?.alert_message ?? undefined);
        return false;
      }
      if (!response.klarna_action) {
        captureException(
          new Error('Klarna initiate: Positive response missing klarna_action'),
          { extra: { paymentBatchId: response.payment_batch_id } },
        );
        showErrorToast(i18n.t('klarna.unavailable'));
        return false;
      }
      const action = JSON.parse(response.klarna_action);
      if (action?.type !== 'redirect' || typeof action.url !== 'string') {
        captureException(
          new Error(
            'Klarna initiate: Positive response with malformed klarna_action',
          ),
          {
            extra: {
              actionType: action?.type,
              paymentBatchId: response.payment_batch_id,
            },
          },
        );
        showErrorToast(i18n.t('klarna.unavailable'));
        return false;
      }
      const redirectUrl: string = action.url;
      const paymentBatchId = response.payment_batch_id;

      // Opens Klarna's hosted UI and waits for the user to complete payment.
      const result = await WebBrowser.openAuthSessionAsync(
        redirectUrl,
        returnUrl,
      );
      if (result.type !== 'success') {
        refreshQueries();
        return false;
      }

      const { queryParams } = Linking.parse(result.url);
      const redirectResult = queryParams?.redirectResult;
      if (typeof redirectResult !== 'string') {
        captureException(
          new Error('Klarna native: WebBrowser success without redirectResult'),
          { extra: { paymentBatchId } },
        );
        refreshQueries();
        return false;
      }

      const confirmResponse = await confirm({
        variables: {
          klarnaPaymentDetails: JSON.stringify({ redirectResult }),
          paymentBatchId,
          restaurantId: restaurantUUID,
          supplierId: supplierUUID,
        },
      });
      const confirmData = confirmResponse.data?.confirmKlarnaPayment;
      if (confirmData?.alert_level === AlertLevel.Positive) {
        onKlarnaPayment();
        return true;
      }
      if (
        confirmData?.payment_batch?.result_code !==
        PaymentBatchResultCode.CANCELLED
      ) {
        showErrorToast(confirmData?.alert_message ?? undefined);
      }
      return false;
    },
    [
      confirm,
      initiate,
      klarnaSelection,
      onKlarnaPayment,
      refreshQueries,
      restaurantUUID,
      showErrorToast,
      supplierUUID,
    ],
  );

  return { startFlow };
};
